import type { LucideIcon } from 'lucide-react';
import {
  MousePointer2,
  Pencil,
  Eraser,
  Square,
  CloudFog,
  Ruler,
  Pin,
  DoorOpen,
  Type,
  Swords,
  BoxSelect,
  Target,
  Hexagon,
  Fence,
  Wand2,
  Lasso,
  ZoomIn,
} from 'lucide-react';
import type { WallBlockingType } from '@roll1/shared';
import type { ToolName, ShapeType } from '../types';
import type { TranslationKeys } from '../i18n';

/** Static configuration for a toolbar group. */
export interface ToolGroup {
  /** Unique group ID */
  id: string;
  /** i18n key for group label */
  labelKey: keyof TranslationKeys;
  /** Default Lucide icon (when no sub-tool selected yet) */
  defaultIcon: LucideIcon;
  /** Tools in this group (order = submenu order) */
  tools: ToolName[];
  /** Shortcut key for primary tool */
  shortcutKey?: string;
  /** GM-only group (hidden from players) */
  gmOnly?: boolean;
  /** When true, multi-tool group shows flyout for mode switching instead of submenu strip */
  flyoutModes?: boolean;
}

/** Typed tool options stored in ToolContext.options */
export interface ToolOptions {
  snapToGrid: boolean;
  drawColor: string;
  drawWidth: number;
  shapeType: ShapeType;
  fogMode: 'brush' | 'rect' | 'polygon';
  fogBrushSize: number;
  reFogMode: boolean;
  linkDrawMode: 'rect' | 'polygon' | 'circle';
  wallBlockingType: WallBlockingType;
}

/** Default values for tool options */
export const DEFAULT_TOOL_OPTIONS: ToolOptions = {
  snapToGrid: false,
  drawColor: '#ff0000',
  drawWidth: 3,
  shapeType: 'rect',
  fogMode: 'brush',
  fogBrushSize: 70,
  reFogMode: false,
  linkDrawMode: 'rect',
  wallBlockingType: 'wall' as WallBlockingType,
};

/** Static toolbar group configuration — 6 groups (043-floating-toolbar) */
export const TOOL_GROUPS: ToolGroup[] = [
  {
    id: 'select',
    labelKey: 'toolbar.group.select',
    defaultIcon: MousePointer2,
    tools: ['select'],
    shortcutKey: 'S',
    gmOnly: false,
  },
  {
    id: 'drawing',
    labelKey: 'toolbar.group.drawing',
    defaultIcon: Pencil,
    tools: ['draw', 'erase', 'shape', 'select-drawing', 'text'],
    shortcutKey: 'B',
    gmOnly: true,
  },
  {
    id: 'fog',
    labelKey: 'toolbar.group.fog',
    defaultIcon: CloudFog,
    tools: ['fog'],
    shortcutKey: 'F',
    gmOnly: true,
  },
  {
    id: 'measurement',
    labelKey: 'toolbar.group.measure',
    defaultIcon: Ruler,
    tools: ['measure', 'aoe'],
    shortcutKey: 'R',
    gmOnly: false,
  },
  {
    id: 'note',
    labelKey: 'toolbar.group.note',
    defaultIcon: Pin,
    tools: ['note'],
    shortcutKey: 'N',
    gmOnly: true,
  },
  {
    id: 'objects',
    labelKey: 'toolbar.group.objects',
    defaultIcon: DoorOpen,
    tools: ['location-link', 'map-object', 'wall'],
    shortcutKey: 'L',
    gmOnly: true,
  },
  {
    id: 'ai-edit',
    labelKey: 'toolbar.group.aiEdit',
    defaultIcon: Wand2,
    tools: ['inpaint-rect', 'inpaint-lasso', 'detail-rect'],
    shortcutKey: 'W',
    gmOnly: true,
    flyoutModes: true,
  },
];

/** Map tool name to its Lucide icon */
export const TOOL_ICONS: Record<ToolName, LucideIcon> = {
  select: MousePointer2,
  'select-drawing': BoxSelect,
  draw: Pencil,
  erase: Eraser,
  shape: Square,
  measure: Ruler,
  fog: CloudFog,
  note: Pin,
  'location-link': DoorOpen,
  token: Swords,
  text: Type,
  aoe: Target,
  'map-object': Hexagon,
  wall: Fence,
  'inpaint-rect': BoxSelect,
  'inpaint-lasso': Lasso,
  'detail-rect': ZoomIn,
};

/** Map tool name to its i18n label key */
export const TOOL_LABELS: Record<ToolName, keyof TranslationKeys> = {
  select: 'toolbar.select',
  'select-drawing': 'toolbar.selectDrawing',
  draw: 'toolbar.draw',
  erase: 'toolbar.erase',
  shape: 'toolbar.shape',
  measure: 'toolbar.measure',
  fog: 'toolbar.fog',
  note: 'toolbar.note',
  'location-link': 'toolbar.portal',
  token: 'toolbar.token',
  text: 'toolbar.text',
  aoe: 'toolbar.aoe',
  'map-object': 'toolbar.mapObject',
  wall: 'toolbar.wall',
  'inpaint-rect': 'toolbar.inpaintRect',
  'inpaint-lasso': 'toolbar.inpaintLasso',
  'detail-rect': 'toolbar.detailRect',
};
